import React, { useState } from "react";
import {
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { useGetFundQuery } from "hooks/use-services/fund.service";
import { User } from "types/user";

interface Props {
  fundAccountId: number;
  setSelectedUser: any;
  selectedAccount: any;
  showError?: boolean; // Optional prop to trigger error display
}

const AdminUserSelection = ({
  fundAccountId,
  setSelectedUser,
  showError = false,
}: Props) => {
  const [selectedValue, setSelectedValue] = useState<User | "">("");
  const [hasError, setHasError] = useState(false);

  const {
    data: fundAccount,
    isLoading,
    isSuccess,
  } = useGetFundQuery(fundAccountId || 0, false);

  const handleUserChange = (event: any) => {
    const selectedUser = event.target.value as User;
    setSelectedValue(selectedUser);
    setSelectedUser(selectedUser);

    if (selectedUser) {
      setHasError(false);
    }
  };

  const shouldShowError = (showError || hasError) && !selectedValue;

  const hasNoUsers =
    !isLoading &&
    (!fundAccount?.fundAccountMembers ||
      fundAccount.fundAccountMembers.length === 0);

  return (
    <Grid item xs={12} sm={12} sx={{ px: 1, py: 2 }}>
      <Typography variant="bodyCopyRegular">
        You are logged in as an admin, please choose a fund's user to notify
        with the request.
      </Typography>

      <FormControl fullWidth sx={{ mt: 1 }} error={shouldShowError}>
        <Select
          fullWidth
          name="user"
          size="small"
          value={selectedValue}
          displayEmpty
          onChange={handleUserChange}
          disabled={isLoading || hasNoUsers}
          startAdornment={
            isLoading ? (
              <CircularProgress
                size={16}
                color="inherit"
                sx={{ ml: 1, mr: 1 }}
              />
            ) : null
          }
          renderValue={(selected) => {
            if (isLoading) {
              return <span style={{ color: "#999" }}>Loading users...</span>;
            }
            if (!selected) {
              return <em style={{ color: "#999" }}>Select User</em>;
            }
            return `${(selected as User).firstName} ${
              (selected as User).lastName
            }`;
          }}
        >
          <MenuItem value="" disabled>
            <em>Select User</em>
          </MenuItem>

          {isLoading ? (
            <MenuItem disabled>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "16px",
                }}
              >
                <CircularProgress size={20} color="primary" />
                <span style={{ marginLeft: "8px", color: "#666" }}>
                  Loading users...
                </span>
              </div>
            </MenuItem>
          ) : isSuccess && hasNoUsers ? (
            <MenuItem disabled>
              <span style={{ color: "#999", fontStyle: "italic" }}>
                No users available
              </span>
            </MenuItem>
          ) : (
            fundAccount?.fundAccountMembers?.map((member, index) => (
              <MenuItem key={index} value={member.user as any}>
                {member.user.firstName} {member.user.lastName}
              </MenuItem>
            ))
          )}
        </Select>

        {shouldShowError && (
          <FormHelperText>Please select a user to proceed</FormHelperText>
        )}

        {!isLoading && hasNoUsers && isSuccess && (
          <FormHelperText>No users found for this fund account</FormHelperText>
        )}
      </FormControl>
    </Grid>
  );
};

export default AdminUserSelection;
